
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Copy, Check } from 'lucide-react';
import { CampaignFormData } from '../types';

interface ExportDataDialogProps {
  isOpen: boolean;
  onClose: () => void;
  formData: CampaignFormData;
}

const ExportDataDialog: React.FC<ExportDataDialogProps> = ({ isOpen, onClose, formData }) => {
  const [copied, setCopied] = React.useState(false);

  const formatDataForExport = (data: CampaignFormData): string => {
    // Create a clean version of the data for export
    const exportData = {
      ...data,
      // Ensure uploaded documents are properly formatted
      documents_and_links: {
        ...data.documents_and_links,
        uploaded_documents: [] // Clear uploaded documents for demo data
      }
    };

    // Convert to properly formatted JavaScript code
    return `import { CampaignFormData } from '../types';

export const getDemoData = (): CampaignFormData => (${JSON.stringify(exportData, null, 2)});`;
  };

  const exportedCode = formatDataForExport(formData);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(exportedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Export Current Campaign Data</DialogTitle>
          <DialogDescription>
            Copy this code and replace the content in src/utils/campaignDemoData.ts
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-neutral-600">
              Ready to paste into campaignDemoData.ts
            </span>
            <Button
              onClick={handleCopy}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              {copied ? (
                <>
                  <Check size={16} />
                  Copied!
                </>
              ) : (
                <>
                  <Copy size={16} />
                  Copy Code
                </>
              )}
            </Button>
          </div>
          
          <div className="flex-1 border rounded-md overflow-hidden">
            <textarea
              value={exportedCode}
              readOnly
              className="w-full h-full p-4 font-mono text-sm bg-neutral-50 border-0 resize-none focus:outline-none"
              style={{ minHeight: '400px' }}
            />
          </div>
        </div>
        
        <div className="pt-4 border-t">
          <Button onClick={onClose} variant="outline" className="w-full">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportDataDialog;
